import { NotificationService } from "../../services/notifications/service";
import { mapNotificationData } from "../../domain/notifications/mapper.js";
const companyId = process.env.VUE_APP_COMPANY_ID;
import { onConfig } from "../../config.js";

let notificationService = null;

onConfig((config) => {
  notificationService = new NotificationService(
    mapNotificationData,
    config.VUE_APP_BASIC_URL
  );
});

const getDefaultState = () => {
  return {
    selectedNotifications: [],
    notifications: [],
    fetchLoadingState: false,
    fetchError: null,
    updateLoadingState: false,
    updateError: null,
  };
};

const state = getDefaultState();

const getters = {
  allNotificationsSelected: (state) => {
    return state.notifications.length > 0 && state.notifications.every((notification) => state.selectedNotifications.has(notification.alertRule.id));
  },
  noNotificationsSelected: (state) => {
    return state.notifications.length > 0 && state.selectedNotifications.length === 0;
  },
  emptyNotifications: (state) => {
    return state.notifications.length === 0;
  },
  groupedNotificationsByRule: (state) => {

    const groups = {};

    const now = new Date();
    const cutoffDate = new Date();
    cutoffDate.setMonth(now.getMonth() - 2);

    // Check if company 4 (all-time) or limited to 2 months
    const isUnlimitedCompany = companyId === "4";

    state.notifications.forEach((notification) => {

      const notificationDate = new Date(notification.checkDate);

      // Only apply date filter if NOT company 4
      if (!isUnlimitedCompany && notificationDate < cutoffDate) return;

      const ruleId = notification.alertRule.id;
      const vesselId = notification.alertToBeExecuted.vesselId;
      const vesselName = notification.alertToBeExecuted.vesselName;

      //  group by rule + vessel
      const groupKey = ruleId + "_" + vesselId;

      if (!groups[groupKey]) {
        groups[groupKey] = {
          latestDate: notification.checkDate,
          alertRule: notification.alertRule,
          vesselId,
          vesselName,
          _notifications: []
        };
      }

      groups[groupKey]._notifications.push(notification);

      if (
        notificationDate >
                new Date(groups[groupKey].latestDate)
      ) {
        groups[groupKey].latestDate = notification.checkDate;
      }
    });

    return Object.values(groups).map((group) => {

      if (!group._notifications.length) return null;

      // collapse same-day duplicates
      const byDay = {};

      group._notifications.forEach((n) => {
        const day = new Date(n.checkDate)
          .toISOString()
          .substring(0, 10);

        if (!byDay[day]) {
          byDay[day] = {
            startDate: day,
            endDate: day,
            notifications: [n]
          };
        } else {
          byDay[day].notifications.push(n);
        }
      });

      const uniqueDays = Object.values(byDay);
      if (!uniqueDays.length) return null;

      uniqueDays.sort(
        (a, b) => new Date(a.startDate) - new Date(b.startDate)
      );

      const streaks = [];
      let current = { ...uniqueDays[0] };

      for (let i = 1; i < uniqueDays.length; i++) {

        const diffDays =
                    (new Date(uniqueDays[i].startDate) -
                        new Date(current.endDate)) /
                    (1000 * 60 * 60 * 24);

        if (Math.round(diffDays) === 1) {
          current.endDate = uniqueDays[i].startDate;
          current.notifications.push(
            ...uniqueDays[i].notifications
          );
        } else {
          streaks.push(current);
          current = { ...uniqueDays[i] };
        }
      }

      streaks.push(current);

      return {
        alertRule: group.alertRule,
        vesselId: group.vesselId,
        vesselName: group.vesselName,   //  vessel name included
        notifications: group._notifications,
        latestDate: group.latestDate,
        //notifications: streaks,
        count: streaks.length
      };
    })
      .filter(Boolean)
      .sort(
        (a, b) => new Date(b.latestDate) - new Date(a.latestDate)
      );
  },
  groupedNotificationsByVessel: (state) => {
    const groups = {};

    state.notifications.forEach((notification) => {
      const vesselId = notification.alertToBeExecuted.vesselId;

      if (!groups[vesselId]) {
        groups[vesselId] = {
          count: 1,
          selected: false,
          latestDate: notification.checkDate,
          notifications: [notification],
          alertRule: notification.alertRule
        };
      } else {
        groups[vesselId].count++;
        groups[vesselId].notifications.push(notification);

        // safer date comparison
        if (
          new Date(notification.checkDate) >
                    new Date(groups[vesselId].latestDate)
        ) {
          groups[vesselId].latestDate = notification.checkDate;
        }
      }
    });

    return Object.values(groups).sort(
      (a, b) => new Date(b.latestDate) - new Date(a.latestDate)
    );
  },
  groupedNotificationCount: (state) => {
    if (!state.notifications.length) return 0;

    //  last 2 months cutoff
    const now = new Date();
    const cutoffDate = new Date();
    cutoffDate.setMonth(now.getMonth() - 2);

    // Check if company 4 (all-time) or limited to 2 months
    const isUnlimitedCompany = companyId === "4";

    // collapse same-day duplicates
    const byDayAndRuleAndVessel = {};

    state.notifications.forEach((n) => {

      const notificationDate = new Date(n.checkDate);

      // Only apply date filter if NOT company 4
      if (!isUnlimitedCompany && notificationDate < cutoffDate) return;

      const day = notificationDate
        .toISOString()
        .substring(0, 10);

      const key =
                n.alertRule.id +
                "_" +
                n.alertToBeExecuted.vesselId +
                "_" +
                day;

      if (!byDayAndRuleAndVessel[key]) {
        byDayAndRuleAndVessel[key] = {
          ruleId: n.alertRule.id,
          vesselId: n.alertToBeExecuted.vesselId,
          checkDate: day
        };
      }
    });

    const uniquePerDay = Object.values(byDayAndRuleAndVessel);

    // sort by rule + vessel + date
    uniquePerDay.sort((a, b) => {

      if (a.vesselId !== b.vesselId)
        return a.vesselId - b.vesselId;

      if (a.ruleId !== b.ruleId)
        return a.ruleId - b.ruleId;

      return new Date(a.checkDate) - new Date(b.checkDate);
    });

    let count = 0;
    let current = null;

    uniquePerDay.forEach((item) => {

      if (!current) {
        current = item;
        count++;
        return;
      }

      const sameRule = item.ruleId === current.ruleId;
      const sameVessel = item.vesselId === current.vesselId;

      const diffDays =
                (new Date(item.checkDate) - new Date(current.checkDate)) /
                (1000 * 60 * 60 * 24);

      if (sameRule && sameVessel && diffDays === 1) {
        current = item; // extend streak
      } else {
        count++;
        current = item;
      }
    });

    return count;
  }

};

const actions = {
  async fetchNotifications({ commit }) {
    commit("SET_FETCH_LOADING_STATE");
    try {
      const notifications = await notificationService.getMany(
        localStorage.getItem("quad_token"),
      );
      commit("SET_FETCH_SUCCESS_STATE", notifications);
    } catch (error) {
      commit("SET_FETCH_ERROR_STATE", error);
    }
  },

  async updateNotifications({ commit }) {
    commit("SET_UPDATE_LOADING_STATE");
    try {
      const updatedNotifications = await notificationService.updateMany(
        localStorage.getItem("quad_token"),
        state.selectedNotifications
      );
      commit("SET_UPDATE_SUCCESS_STATE", updatedNotifications);
    } catch (error) {
      commit("SET_UPDATE_ERROR_STATE", error);
    }
  }
};

const mutations = {
  SET_FETCH_LOADING_STATE(state) {
    state.fetchLoadingState = true;
    state.fetchError = null;
  },
  SET_FETCH_ERROR_STATE(state, error) {
    state.fetchLoadingState = false;
    state.fetchError = error;
  },
  SET_FETCH_SUCCESS_STATE(state, notifications) {
    state.fetchLoadingState = false;
    state.notifications = notifications;
  },
  SET_UPDATE_LOADING_STATE(state) {
    state.updateLoadingState = true;
    state.updateError = null;
  },
  SET_UPDATE_ERROR_STATE(state, error) {
    state.updateLoadingState = false;
    state.updateError = error;
  },
  SET_UPDATE_SUCCESS_STATE(state, notifications) {
    state.updateLoadingState = false;
    state.notifications = notifications;
    state.selectedNotifications = [];
  },
  SET_SELECTED_NOTIFICATIONS(state, selectedNotifications) {
    state.selectedNotifications = selectedNotifications;
  },
  SELECT_ALL_NOTIFICATIONS(state) {
    state.selectedNotifications = state.notifications;
  },
  ADD_SELECTED_NOTIFICATIONS(state, selectedNotifications) {
    selectedNotifications.forEach((notification) => {
      if (!state.selectedNotifications.includes(notification)) {
        state.selectedNotifications.push(notification);
      }
    });
  },
  REMOVE_SELECTED_NOTIFICATIONS(state, selectedNotifications) {
    selectedNotifications.forEach((notification) => {
      state.selectedNotifications = state.selectedNotifications.filter((n) => n.id !== notification.id);
    });
  }
};

export const notificationsModule = {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
